import { getFromDataPath, mutateByPath } from "../../../utility";
import { isGroupingFactor } from "../../typeGuards";
import { ArsTableState, ARSTActionDropAction } from "../interface";
import { isArsTableSubGroup } from "../typeguards";
import { arst_groupingFactorToTableGroup } from "./arst_arsToTableGroup";
import { arst_checkSiblingSubgroups } from "./arst_checkSiblingSubgroups";

/**
 * Inserts dropped grouping factors as subgroups on the targeted table group.
 *
 * @param state - Current ARS table state.
 * @param action - Drop action containing target cell location and payload.
 * @returns Updated ARS table state with appended subgroup entries when valid.
 */
export const arst_insertSubgroup = <T>(
  state: ArsTableState<T>,
  action: ARSTActionDropAction,
): ArsTableState<T> => {
  // Find target group(s), including sibling groups with matching subgroup structures.
  const targetCell = state.scaffoldModel?.cellData?.[action.rowId]?.[action.columnId];
  if (!targetCell) return state;
  const targetGroups = arst_checkSiblingSubgroups(
    state.bodyData,
    targetCell.dataPath,
    state.settings.editorProps.isT,
  );
  if (!targetGroups) return state;

  const newTableGroups = action.payload
    .map((item) => item.dropItem)
    .filter((item) => isGroupingFactor(item))
    .map((groupingFactor) =>
      arst_groupingFactorToTableGroup(groupingFactor, state.settings.editorProps),
    );

  // Append to each resolved target path.
  const newBodyData = Object.keys(targetGroups).reduce((currentBodyData, groupPath) => {
    const currentGroup = getFromDataPath(groupPath, currentBodyData, (obj) =>
      isArsTableSubGroup(obj, state.settings.editorProps.isT),
    );
    // istanbul ignore next -- resolved paths are validated by arst_checkSiblingSubgroups
    if (!currentGroup) return currentBodyData;
    const newSubGroups = [...(currentGroup.subGroups ?? []), ...newTableGroups];
    return mutateByPath(currentBodyData, groupPath + "/subGroups", newSubGroups);
  }, state.bodyData);

  return {
    ...state,
    bodyData: newBodyData,
    changed: true,
  };
};
